import { UserService } from 'go-modules/models/user/user.service';
import { filter, takeUntil } from 'rxjs/operators';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { CommentFilterService } from '@go-modules/services/comment-filter/comment-filter.service';
import { SelectedService } from 'go-modules/services/selected/selected.service';
import { Subject, Subscription } from 'rxjs';
import { FeedbackSessionService, FeedbackTabs } from '@go-modules/services/feedback-session/feedback-session.service';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';

export class FeedbackDisplayController {
	public initialFeedbackLoaded: boolean = false;
	public environmentVarsService: EnvironmentVarsService;
	public readonly componentDestroyed$$ = new Subject();
	public activeTab: FeedbackTabs;
	public tabs = FeedbackTabs;
	private autoGradedRubricStateOnlySelected = false;
	private eventSubscription: Subscription;

	/* @ngInject */
	constructor (
		public $scope,
		private feedbackSession,
		public ngxCommentFilterService: CommentFilterService,
		private userService: UserService,
		private selectedService: SelectedService,
		private ngxFeedbackSessionService: FeedbackSessionService,
		private eventService: EventService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public $onInit (): void {
		this.$scope.$watch('mainController.activity', this.activityWatch);

		this.ngxFeedbackSessionService.activeTab$.pipe(takeUntil(this.componentDestroyed$$)).subscribe((tab) => {
			this.activeTab = tab;
			this.safeDigest();
		});

		this.ngxFeedbackSessionService.initializeState();

		this.eventSubscription = this.eventService.events.pipe(
			filter((event) => event.name === EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED ||
				event.name === EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_AUTO_GRADED_STATE_ONLY_SELECTED)
		).subscribe((event) => {
			if (event.name === EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_LOADED) {
				this.initialFeedbackLoaded = true;
			}

			if (event.name === EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_AUTO_GRADED_STATE_ONLY_SELECTED) {
				this.autoGradedRubricStateOnlySelected = event.data.selected;
			}

			this.safeDigest();
		});
	}

	public $onDestroy (): void {
		this.eventSubscription?.unsubscribe();
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
	}

	public showFeedbackCreator = () => {
		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return false;
		}

		if (this.activeTab === FeedbackTabs.RUBRIC && this.autoGradedRubricStateOnlySelected) {
			return false;
		}

		const { activity, session, user, license, userGroup } = this.$scope.mainController;
		if (session.isLimitCommentOnlySingleAttempt(activity, user)) {
			return false;
		}

		if (!userGroup.validateCanCreateSession(user, license).isValid) {
			return false;
		}

		return this.$scope.mainController.activity.isCommentsEnabled()
			&& (!this.feedbackSession.isRecording() || this.$scope.mainController.activity.live_session_enabled);
	};

	public removeRubric = (rubric) => {
		this.$scope.contentController.removeFeedbackItem(rubric);
	};

	public addRubric = (rubric) => {
		this.$scope.contentController.addFeedbackItem(rubric);
	};

	private activityWatch = () => {
		if (this.canSeeCommentsOrRubrics()) {
			const activity = this.$scope.mainController.activity;

			if (this.feedbackSession.isRecording()) {
				if (activity.hasRecordingInstructions()) {
					this.ngxFeedbackSessionService.setActiveTab(FeedbackTabs.INFORMATION);
				}
			} else {
				this.ngxFeedbackSessionService.setActiveTab(FeedbackTabs.COMMENT);
			}
		} else {
			this.ngxFeedbackSessionService.setActiveTab(FeedbackTabs.INFORMATION);
		}
	};

	public canSeeCommentsOrRubrics = (): boolean => {
		const { activity } = this.$scope.mainController;
		const isRecordMode = this.feedbackSession.isRecording();
		const hasLiveSession = activity.live_session_enabled;

		if (isRecordMode && !hasLiveSession) {
			return false;
		}

		if (activity.isCommentsEnabled()) {
			return true;
		}

		if (activity.isPeerRubricEnabled()) {
			return true;
		}

		if (activity.isRubricEnabled()) {
			const group = this.selectedService.getGroup();
			const session = this.selectedService.getSession();
			const user = this.userService.currentUser;
			return group.hasReviewerRole(true) || session.isOwner(user);
		}

		return false;
	};

	private safeDigest () {
		const phase = this.$scope.$root.$$phase || this.$scope.$$phase;
		if (!phase) {
			this.$scope.$digest();
		}
	}
}
